import React, { useCallback, useEffect, useState } from "react";
import Select from "react-select";
import "./SignalDashboard.css"; // uses your global-toolbar styles
import { buildScopedApiUrl, ensureArray, getApiHeaders } from "./config";
import { useWorkspace } from "./workspace/WorkspaceContext";

const timeFrameOptions = [
  { label: "5 min", value: 5 },
  { label: "15 min", value: 15 },
  { label: "30 min", value: 30 },
  { label: "1 hour", value: 60 },
  { label: "4 hour", value: 240 },
  { label: "8 hour", value: 480 },
  { label: "12 hour", value: 720 },
  { label: "1 day", value: 1440 },
];

const HistoryFilterBar = ({ onFetchHistory }) => {
  const { workspace } = useWorkspace();
  const [coinOptions, setCoinOptions] = useState([]);
  const [selectedSymbols, setSelectedSymbols] = useState([]);
  const [selectedTimeFrames, setSelectedTimeFrames] = useState([]);
  const [selectAllSymbols, setSelectAllSymbols] = useState(false);
  const [selectAllTimeFrames, setSelectAllTimeFrames] = useState(false);
  const [noOfPrevCandles, setNoOfPrevCandles] = useState(null);
  const [cutOffInHours, setCutOffInHours] = useState(null);
  const [loadingSymbols, setLoadingSymbols] = useState(false);

  /* =========================
     1️⃣ Fetch Symbol Options
  ========================== */
  const fetchSymbols = useCallback(async () => {
      setLoadingSymbols(true);
      try {
        const res = await fetch(
          buildScopedApiUrl('/api/bot/ui/get/getRecentActiveAlertSymbols', workspace),
          {
            headers: getApiHeaders()
          }
        );
        const data = await res.json();
        const safeSymbols = ensureArray(data);
        if (!Array.isArray(data)) {
          console.warn("Unexpected history symbol payload shape", workspace, data);
        }
        const formatted = safeSymbols.map((s) => ({ label: s, value: s }));
        setCoinOptions(formatted);
      } catch (err) {
        console.error("Failed to fetch symbols:", err);
      } finally {
        setLoadingSymbols(false);
      }
    }, [workspace]);

  useEffect(() => {
    fetchSymbols();
  }, [fetchSymbols]);

  /* =========================
     2️⃣ Handle Select-All Toggles
  ========================== */
  const handleSelectAllSymbols = () => {
    if (selectAllSymbols) {
      // unselect all
      setSelectedSymbols([]);
      setSelectAllSymbols(false);
    } else {
      // selecting all symbols disables timeframe select all
      setSelectAllSymbols(true);
      setSelectAllTimeFrames(false);
      setSelectedSymbols(ensureArray(coinOptions).map((s) => s.value));
    }
  };

  const handleSelectAllTimeFrames = () => {
    if (selectAllTimeFrames) {
      setSelectedTimeFrames([]);
      setSelectAllTimeFrames(false);
    } else {
      // selecting all timeframes disables symbol select all
      setSelectAllTimeFrames(true);
      setSelectAllSymbols(false);
      setSelectedTimeFrames(timeFrameOptions.map((t) => t.value));
    }
  };

  /* =========================
     3️⃣ Numeric Dropdown Options
  ========================== */
  const candleOptions = [1, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50].map((n) => ({
    label: n.toString(),
    value: n,
  }));

  const cutoffOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 24, 48].map((n) => ({
    label: `${n}h`,
    value: n,
  }));

  /* =========================
     4️⃣ Shared Styles
  ========================== */
  const selectStyles = {
    control: (base) => ({
      ...base,
      backgroundColor: "#1e293b",
      borderColor: "#334155",
      borderWidth: 1,
      minHeight: 38,
      boxShadow: "none",
      ":hover": { borderColor: "#64748b" },
    }),
    input: (base) => ({ ...base, color: "white" }),
    placeholder: (base) => ({ ...base, color: "#64748b" }),
    menu: (base) => ({
      ...base,
      backgroundColor: "#1e293b",
      color: "white",
      zIndex: 9999,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? "#334155" : "#1e293b",
      color: "white",
      cursor: "pointer",
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: "#0f172a",
      border: "1px solid #475569",
    }),
    multiValueLabel: (base) => ({ ...base, color: "#e2e8f0" }),
    multiValueRemove: (base) => ({
      ...base,
      color: "#94a3b8",
      ":hover": { backgroundColor: "#475569", color: "white" },
    }),
    singleValue: (base) => ({ ...base, color: "#e2e8f0" }),
  };

  /* =========================
     5️⃣ Handler: Fetch Button
  ========================== */
  const handleFetch = () => {
    if (!selectedSymbols.length && !selectAllSymbols) {
      alert("Please select at least one symbol or Select All Symbols.");
      return;
    }
    if (!selectedTimeFrames.length && !selectAllTimeFrames) {
      alert("Please select at least one timeframe or Select All TimeFrames.");
      return;
    }
    if (noOfPrevCandles && cutOffInHours) {
      alert("Please fill either No. of Previous Candles OR Cut-off in Hours, not both.");
      return;
    }
    onFetchHistory(
      selectedSymbols,
      selectedTimeFrames,
      noOfPrevCandles,
      cutOffInHours
    );
  };

  /* =========================
     6️⃣ Render
  ========================== */
  return (
    <div className="history-filter-bar">
      <div className="hfb-field">
        <label>Workspace</label>
        <div className="filter-context-select">
          {workspace.exchange} / {workspace.marketType}
        </div>
      </div>

      {/* ---- SYMBOL SELECT ---- */}
      <div className="hfb-field hfb-field-wide">
        <label>
          Symbols
          <label style={{ fontSize: 11, marginLeft: 10, fontWeight: 400, textTransform: "none", letterSpacing: 0, color: "#94a3b8" }}>
            <input
              type="checkbox"
              checked={selectAllSymbols}
              disabled={selectAllTimeFrames}
              onChange={handleSelectAllSymbols}
              style={{ marginRight: 4 }}
            />
            Select All
          </label>
        </label>
        <Select
          isMulti
          isDisabled={selectAllSymbols}
          placeholder={loadingSymbols ? "Loading symbols..." : "Select Symbols..."}
          options={coinOptions}
          value={ensureArray(selectedSymbols).map((s) => ({ label: s, value: s }))}
          onChange={(selected) =>
            setSelectedSymbols(ensureArray(selected).map((x) => x.value))
          }
          styles={selectStyles}
        />
      </div>

      {/* ---- TIMEFRAME SELECT ---- */}
      <div className="hfb-field">
        <label>
          Time Frames
          <label style={{ fontSize: 11, marginLeft: 10, fontWeight: 400, textTransform: "none", letterSpacing: 0, color: "#94a3b8" }}>
            <input
              type="checkbox"
              checked={selectAllTimeFrames}
              disabled={selectAllSymbols}
              onChange={handleSelectAllTimeFrames}
              style={{ marginRight: 4 }}
            />
            Select All
          </label>
        </label>
        <Select
          isMulti
          isDisabled={selectAllTimeFrames}
          placeholder="Select Time Frames..."
          options={timeFrameOptions}
          value={ensureArray(selectedTimeFrames).map((v) => ({
            label:
              timeFrameOptions.find((t) => t.value === v)?.label ||
              `${v} min`,
            value: v,
          }))}
          onChange={(selected) =>
            setSelectedTimeFrames(ensureArray(selected).map((x) => x.value))
          }
          styles={selectStyles}
        />
      </div>

      {/* ---- PREV CANDLES ---- */}
      <div className="hfb-field">
        <label>No. of Prev Candles</label>
        <Select
          isClearable
          isSearchable
          placeholder="Select count..."
          options={candleOptions}
          value={
            noOfPrevCandles
              ? { label: noOfPrevCandles.toString(), value: noOfPrevCandles }
              : null
          }
          onChange={(option) => {
            setNoOfPrevCandles(option ? option.value : null);
            if (option) setCutOffInHours(null);
          }}
          styles={selectStyles}
          isDisabled={!!cutOffInHours}
        />
      </div>

      {/* ---- CUT-OFF HOURS ---- */}
      <div className="hfb-field">
        <label>Cut-off in Hours</label>
        <Select
          isClearable
          isSearchable
          placeholder="Select hours..."
          options={cutoffOptions}
          value={
            cutOffInHours
              ? { label: `${cutOffInHours}h`, value: cutOffInHours }
              : null
          }
          onChange={(option) => {
            setCutOffInHours(option ? option.value : null);
            if (option) setNoOfPrevCandles(null);
          }}
          styles={selectStyles}
          isDisabled={!!noOfPrevCandles}
        />
      </div>

      {/* ---- FETCH BUTTON ---- */}
      <button className="history-filter-fetch-btn" onClick={handleFetch}>
        Fetch Historical Signals
      </button>
    </div>
  );
};

export default HistoryFilterBar;
